import axios, { AxiosError, AxiosRequestConfig, Method } from "axios";
import { FPG_PROXY_PATH, FPG_UNAUTH_PROXY_PATH, delay } from "../utils";
import {
  CreateFordStandardErrorProps,
  createFordStandardError,
} from "./createFordStandardError";

interface DEFAULT_AXIOS_OPTIONS_TYPE extends AxiosRequestConfig {
  retryWait: number[];
  responseType: undefined;
  method: string;
  data: null;
  errorsToRetry: number[];
  shouldRetryWhenTimeout: boolean;
  timeout: number;
}

export const DEFAULT_AXIOS_OPTIONS: DEFAULT_AXIOS_OPTIONS_TYPE = {
  timeout: 5000,
  shouldRetryWhenTimeout: true,
  retryWait: [100, 1000],
  responseType: undefined,
  method: "get",
  data: null,
  errorsToRetry: [],
};

const CUSTOM_AEM_STATUS_MESSAGE_ID = {
  TIMEOUT: "TIMEOUT",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
  EMPTY_RESULT_SET: "EMPTY_RESULT_SET",
};

const makeApiRequest = async <T>(method: Method, url: string) =>
  url.indexOf(FPG_PROXY_PATH) > -1 && url.indexOf(FPG_UNAUTH_PROXY_PATH) < 0
    ? await axios<T>(url, {
        ...DEFAULT_AXIOS_OPTIONS,
        method: method,
        withCredentials: true,
      })
    : await axios<T>(url, {
        method: method,
      });

const isTimeout = <ResponseType, RequestDataType>(
  axiosError: AxiosError<ResponseType, RequestDataType>,
) => axiosError?.message?.toLowerCase()?.includes("timeout");

const generateError = <ResponseType, RequestDataType>(
  axiosError: AxiosError<ResponseType, RequestDataType>,
  customErrorId: string,
): Partial<CreateFordStandardErrorProps<ResponseType>> => {
  return {
    message: axiosError.message,
    customErrorCode: customErrorId,
  };
};

const isNoResponse = <ResponseType, RequestDataType>(
  axiosError: AxiosError<ResponseType, RequestDataType>,
) => !axiosError?.response;

interface ApiBackendResponseType {
  message: {
    code: string;
  };
}

const isEmptyResultSetResponse = <RequestDataType>(
  axiosError: AxiosError<ApiBackendResponseType, RequestDataType>,
): boolean => {
  const data = axiosError?.response?.data;
  const code = data?.message?.code ?? "";
  return code === CUSTOM_AEM_STATUS_MESSAGE_ID.EMPTY_RESULT_SET;
};

const isNon500Error = <ResponseType, RequestDataType>(
  axiosError: AxiosError<ResponseType, RequestDataType>,
) => axiosError?.response?.status !== 500;
const containsErrorToRetry = <ResponseType, RequestDataType>(
  axiosError: AxiosError<ResponseType, RequestDataType>,
  errorsToRetry: number[],
) => {
  if (errorsToRetry?.length === 0) return false;
  return errorsToRetry.includes(axiosError?.response?.status ?? 0);
};

export const getWithRetry = async <ResponseType, RequestDataType>(
  url: string,
  options = {},
) => {
  const effectiveOptions = {
    ...DEFAULT_AXIOS_OPTIONS,
    ...options,
  };
  const { shouldRetryWhenTimeout, retryWait, errorsToRetry, ...axiosOptions } =
    effectiveOptions;
  const maxTries = retryWait.length + 1;

  let tryCount = 0;
  let error:
    | AxiosError<ResponseType, RequestDataType>
    | Partial<CreateFordStandardErrorProps<ResponseType>> = {};

  while (tryCount < maxTries) {
    if (tryCount > 0) {
      await delay(retryWait[tryCount - 1]);
    }

    try {
      return url.indexOf(FPG_PROXY_PATH) > -1 &&
        url.indexOf(FPG_UNAUTH_PROXY_PATH) < 0
        ? await axios<ResponseType>(url, {
            ...axiosOptions,
            withCredentials: true,
          })
        : await axios<ResponseType>(url, axiosOptions);
    } catch (axiosError) {
      const err: AxiosError<ResponseType, RequestDataType> =
        axiosError as AxiosError<ResponseType, RequestDataType>;
      if (isTimeout(err)) {
        error = generateError(err, CUSTOM_AEM_STATUS_MESSAGE_ID.TIMEOUT);
        if (!shouldRetryWhenTimeout) break;
      } else if (isNoResponse(err)) {
        error = generateError(err, CUSTOM_AEM_STATUS_MESSAGE_ID.UNKNOWN_ERROR);
      } else if (
        isEmptyResultSetResponse(
          err as AxiosError<ApiBackendResponseType, RequestDataType>,
        )
      ) {
        error = generateError(
          err,
          CUSTOM_AEM_STATUS_MESSAGE_ID.EMPTY_RESULT_SET,
        );
      } else {
        error = err;
        if (isNon500Error(err) && !containsErrorToRetry(err, errorsToRetry))
          break;
      }
    }

    console.warn(`FPT: HTTP error ${JSON.stringify(error)}`);
    tryCount += 1;
  }

  const potentialFordError = error as Partial<
    CreateFordStandardErrorProps<ResponseType>
  >;
  const potentialAxiosError = error as AxiosError<
    ResponseType,
    RequestDataType
  >;

  const response = potentialAxiosError.response;
  const originalResponse = response?.data;
  const newVar: CreateFordStandardErrorProps<ResponseType> = {
    message: potentialFordError.message,
    originalResponse: originalResponse,
    status: response?.status,
    customErrorCode: potentialFordError.customErrorCode,
  };

  throw createFordStandardError<ResponseType>(newVar);
};

export { makeApiRequest };
